import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2,
  Mail,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  UserCheck,
  Shield,
  Fingerprint,
  Eye,
  EyeOff,
} from 'lucide-react';

const REMEMBER_EMAIL_KEY = 'factory_remember_email';

type AuthMode = 'select' | 'login' | 'register' | 'confirm' | 'forgot' | 'reset';

/* Google "G" icon */
function GoogleIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" fill="#34A853"/>
      <path d="M5.84 14.09A6.97 6.97 0 0 1 5.48 12c0-.72.13-1.43.36-2.09V7.07H2.18A11.96 11.96 0 0 0 .96 12c0 1.94.46 3.77 1.22 5.33l3.66-3.24Z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" fill="#EA4335"/>
    </svg>
  );
}

/* ── Floating Particles Background ── */
function AmbientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const particles = useMemo(() => {
    return Array.from({ length: 60 }, () => ({
      x: Math.random(),
      y: Math.random(),
      size: Math.random() * 1.5 + 0.5,
      speedX: (Math.random() - 0.5) * 0.15,
      speedY: (Math.random() - 0.5) * 0.15,
      opacity: Math.random() * 0.3 + 0.05,
      phaseOffset: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    let time = 0;
    const animate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      time += 0.003;

      for (const p of particles) {
        const px = ((p.x + Math.sin(time + p.phaseOffset) * p.speedX * 2 + 1) % 1) * w;
        const py = ((p.y + Math.cos(time * 0.7 + p.phaseOffset) * p.speedY * 2 + 1) % 1) * h;
        const flickerOpacity = p.opacity * (0.6 + 0.4 * Math.sin(time * 2 + p.phaseOffset));

        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 178, 200, ${flickerOpacity})`;
        ctx.fill();
      }

      ctx.strokeStyle = 'rgba(0, 178, 200, 0.02)';
      ctx.lineWidth = 0.5;
      const gridSize = 80;
      for (let x = 0; x < w; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, [particles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}

/* ── Colony-styled text input with optional password toggle ── */
function ColonyInput({
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  centered,
  testId,
}: {
  id: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  centered?: boolean;
  testId?: string;
}) {
  const [showPw, setShowPw] = useState(false);
  const isPw = type === 'password';

  return (
    <div className="relative flex items-center h-10 rounded-[8px] bg-[#151518] border border-[#333338] focus-within:border-[#00B2C8]/50 focus-within:shadow-[0_0_0_3px_rgba(0,178,200,0.08)] transition-all duration-150">
      <input
        id={id}
        name={id}
        type={isPw && showPw ? 'text' : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        data-testid={testId}
        className={`flex-1 h-full bg-transparent px-3 text-[13px] text-[#F3F4F6] placeholder:text-[#9CA3AF] outline-none rounded-[8px] ${
          centered ? 'text-center text-xl tracking-widest' : ''
        }`}
      />
      {isPw && (
        <button
          type="button"
          onClick={() => setShowPw(!showPw)}
          className="flex items-center justify-center w-9 h-full text-[#9CA3AF] hover:text-[#D1D5DB] transition-colors cursor-pointer"
          tabIndex={-1}
        >
          {showPw ? <EyeOff size={15} strokeWidth={1.5} /> : <Eye size={15} strokeWidth={1.5} />}
        </button>
      )}
    </div>
  );
}

/* ── Auth method button for 'select' screen ── */
function AuthMethodButton({
  icon,
  label,
  comingSoon,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  comingSoon?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={comingSoon ? undefined : onClick}
      disabled={comingSoon}
      className={`relative w-full flex items-center justify-center gap-2.5 h-10 rounded-[8px] text-[13px] font-medium transition-all duration-150 ${
        comingSoon
          ? 'bg-[#151518] border border-[rgba(255,255,255,0.06)] text-[#9CA3AF] cursor-not-allowed'
          : 'bg-[#222226] border border-[rgba(255,255,255,0.08)] text-[#F3F4F6] cursor-pointer hover:bg-[#2A2A2F] hover:border-[rgba(255,255,255,0.10)]'
      }`}
    >
      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">{icon}</span>
      <span>{label}</span>
      {comingSoon && (
        <span className="absolute right-3 text-[9px] font-semibold tracking-wide uppercase text-[#9CA3AF] bg-[#2A2A2F] px-1.5 py-0.5 rounded">
          Soon
        </span>
      )}
    </button>
  );
}

/* ── Main Component ── */
export default function MainMenu() {
  const { login, register, confirmRegistration, forgotPassword, confirmForgotPassword, skipLogin, error, clearError } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (saved) { setEmail(saved); setRememberMe(true); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const testEmail = import.meta.env.VITE_TEST_EMAIL;
  const testPassword = import.meta.env.VITE_TEST_PASSWORD;
  const hasTestAccount = Boolean(testEmail && testPassword);

  const passwordsMatch = password === confirmPassword;
  const showPasswordMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const displayError = error || localError;

  /* ── Handlers ── */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); clearError(); setLocalError('');
    try {
      if (rememberMe) localStorage.setItem(REMEMBER_EMAIL_KEY, email);
      else localStorage.removeItem(REMEMBER_EMAIL_KEY);
      await login(email, password);
    } catch {
    } finally { setIsLoading(false); }
  };

  const handleDemoLogin = async () => {
    if (!hasTestAccount) return;
    setIsDemoLoading(true); clearError(); setLocalError('');
    try { await login(testEmail, testPassword); }
    catch {} finally { setIsDemoLoading(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordsMatch) { setLocalError('Passwords do not match'); return; }
    setIsLoading(true); clearError(); setLocalError('');
    try {
      const result = await register(email, password);
      if (result.needsConfirmation) {
        setMode('confirm');
        setSuccessMessage('Check your email for a confirmation code');
      }
    } catch {} finally { setIsLoading(false); }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); clearError(); setLocalError('');
    try {
      await confirmRegistration(email, confirmationCode);
      setSuccessMessage('Account confirmed! You can now log in.');
      setMode('login'); setConfirmationCode('');
    } catch {} finally { setIsLoading(false); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); clearError(); setLocalError('');
    try {
      await forgotPassword(email);
      setSuccessMessage('Check your email for a reset code');
      setMode('reset');
    } catch {} finally { setIsLoading(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordsMatch) { setLocalError('Passwords do not match'); return; }
    setIsLoading(true); clearError(); setLocalError('');
    try {
      await confirmForgotPassword(email, confirmationCode, password);
      setSuccessMessage('Password reset successful! You can now log in.');
      setMode('login'); setPassword(''); setConfirmPassword(''); setConfirmationCode('');
    } catch {} finally { setIsLoading(false); }
  };

  const switchMode = (m: AuthMode) => {
    clearError(); setLocalError(''); setSuccessMessage(''); setMode(m);
  };

  const backTarget: Record<string, AuthMode> = {
    login: 'select', register: 'select', confirm: 'login', forgot: 'login', reset: 'login',
  };

  const headings: Record<AuthMode, string> = {
    select: 'Enter your Colony',
    login: 'Sign in to Colony',
    register: 'Create your account',
    confirm: 'Verify your email',
    forgot: 'Reset password',
    reset: 'Set new password',
  };

  const subheadings: Record<AuthMode, string> = {
    select: 'AI-powered development platform',
    login: 'AI-powered development platform',
    register: 'Get started with Colony',
    confirm: 'Enter the 6-digit code sent to your email',
    forgot: "We'll send a reset code to your email",
    reset: 'Enter your new password',
  };

  /* Primary button helper */
  const PrimaryBtn = ({ type = 'button' as const, onClick, disabled, loading, children, testId, variant = 'primary' }: {
    type?: 'button' | 'submit'; onClick?: () => void; disabled?: boolean;
    loading?: boolean; children: React.ReactNode; testId?: string; variant?: 'primary' | 'outline';
  }) => (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-full flex items-center justify-center gap-2 h-10 rounded-[8px] text-[13px] font-medium transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
        variant === 'outline'
          ? 'bg-[#222226] border border-[#3CCB7F]/30 text-[#3CCB7F] hover:bg-[#3CCB7F]/10'
          : 'bg-[#00B2C8] text-white hover:bg-[#00c9e1]'
      }`}
      style={variant === 'primary' ? {
        boxShadow: !disabled
          ? '0 0 20px rgba(0, 178, 200, 0.2), inset 0 1px 0 rgba(255,255,255,0.12)'
          : 'inset 0 1px 0 rgba(255,255,255,0.08)',
      } : undefined}
    >
      {loading ? <Loader2 size={15} strokeWidth={2} className="animate-spin" /> : children}
    </button>
  );

  /* Back button helper */
  const BackBtn = ({ target }: { target: AuthMode }) => (
    <button
      type="button"
      onClick={() => switchMode(target)}
      className="flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:text-[#D1D5DB] transition-colors cursor-pointer mb-1"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      Back
    </button>
  );

  /* Label helper */
  const FieldLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
    <label htmlFor={htmlFor} className="text-[13px] font-medium text-[#D1D5DB]">{children}</label>
  );

  return (
    <div
      className="relative flex items-center justify-center min-h-screen overflow-hidden"
      data-testid="page-main-menu"
    >
      {/* Background */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 40%, #1A1A1E 0%, #0F0F11 50%, #0B0B0D 100%)',
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(circle 400px at 50% 35%, rgba(0, 178, 200, 0.03) 0%, transparent 100%)',
      }} />
      <AmbientBackground />

      {/* Card container */}
      <div
        className={`relative z-10 w-full max-w-[400px] mx-4 transition-all duration-700 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
        data-testid="container-auth"
      >
        {/* Card with gradient glow border */}
        <div
          className="rounded-[12px] p-[1px]"
          style={{
            background: 'linear-gradient(180deg, rgba(0, 178, 200, 0.12) 0%, rgba(0, 178, 200, 0.04) 40%, rgba(255, 255, 255, 0.03) 100%)',
          }}
        >
          <div
            className="rounded-[11px] px-8 py-10"
            style={{
              background: 'linear-gradient(180deg, #1A1A1E 0%, #151518 100%)',
              boxShadow: '0 0 80px rgba(0, 178, 200, 0.04), 0 25px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
            }}
          >
            {/* Logo with glow */}
            <div className="flex justify-center mb-5">
              <div className="relative">
                <img
                  src="/colony-logo.png"
                  alt="Colony"
                  className="w-12 h-12 object-contain select-none"
                  style={{ filter: 'brightness(1.1)' }}
                  data-testid="logo-icon"
                />
                <div
                  className="absolute inset-0 -m-3 rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(0, 178, 200, 0.1) 0%, transparent 70%)' }}
                />
              </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-8">
              <h1 className="text-[20px] font-semibold text-[#F3F4F6] mb-1.5" data-testid="text-title">
                {headings[mode]}
              </h1>
              <p className="text-[13px] text-[#9CA3AF]">{subheadings[mode]}</p>
            </div>

            {/* Messages */}
            <AnimatePresence>
              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 p-3 rounded-[8px] bg-[#3CCB7F]/15 border border-[#3CCB7F]/25 flex items-center gap-2"
                  data-testid="message-success"
                >
                  <CheckCircle className="w-4 h-4 text-[#3CCB7F] flex-shrink-0" />
                  <span className="text-[13px] text-[#3CCB7F]">{successMessage}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {displayError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 p-3 rounded-[8px] bg-[#E5534B]/15 border border-[#E5534B]/25 flex items-center gap-2"
                  data-testid="message-error"
                >
                  <AlertCircle className="w-4 h-4 text-[#E5534B] flex-shrink-0" />
                  <span className="text-[13px] text-[#E5534B]">{displayError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Form area ── */}
            <AnimatePresence mode="wait">
              {/* SELECT */}
              {mode === 'select' && (
                <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="space-y-2.5" data-testid="form-select">
                  <AuthMethodButton icon={<GoogleIcon size={16} />} label="Continue with Google" comingSoon />
                  <AuthMethodButton icon={<Mail className="w-4 h-4" />} label="Continue with email" onClick={() => switchMode('login')} />
                  <AuthMethodButton icon={<Shield className="w-4 h-4" />} label="Continue with SAML SSO" comingSoon />
                  <AuthMethodButton icon={<Fingerprint className="w-4 h-4" />} label="Log in with passkey" comingSoon />

                  <div className="flex items-center gap-3 mt-6 mb-2">
                    <div className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
                    <span className="text-[11px] text-[#9CA3AF]">or</span>
                    <div className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
                  </div>

                  <p className="text-center text-[13px] text-[#9CA3AF]">
                    Don't have an account?{' '}
                    <span className="text-[#6B7280] cursor-default" title="Coming soon">
                      Sign up <span className="text-[11px]">(coming soon)</span>
                    </span>
                  </p>
                </motion.div>
              )}

              {/* LOGIN */}
              {mode === 'login' && (
                <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onSubmit={handleLogin} className="space-y-4" data-testid="form-login" method="post" autoComplete="on">
                  <BackBtn target={backTarget[mode]} />

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="email">Email</FieldLabel>
                    <ColonyInput id="email" type="email" autoComplete="username" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required testId="input-email" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="password">Password</FieldLabel>
                    <ColonyInput id="password" type="password" autoComplete="current-password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="input-password" />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.05)] text-[#00B2C8] focus:ring-[#00B2C8]/20 accent-[#00B2C8]"
                    />
                    <label htmlFor="remember-me" className="text-[13px] text-[#9CA3AF] cursor-pointer select-none">
                      Remember me
                    </label>
                  </div>

                  <PrimaryBtn type="submit" disabled={isLoading || isDemoLoading || !email || !password} loading={isLoading} testId="button-login">
                    Sign in <ArrowRight size={14} strokeWidth={1.5} />
                  </PrimaryBtn>

                  {hasTestAccount && (
                    <>
                      <div className="flex items-center gap-3 my-2">
                        <div className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
                        <span className="text-[11px] text-[#9CA3AF]">or</span>
                        <div className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
                      </div>
                      <PrimaryBtn variant="outline" onClick={handleDemoLogin} disabled={isLoading || isDemoLoading} loading={isDemoLoading} testId="button-demo-login">
                        <UserCheck className="w-4 h-4" /> Demo Login
                      </PrimaryBtn>
                    </>
                  )}

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button type="button" onClick={() => switchMode('forgot')} className="text-[12px] text-[#00B2C8] hover:text-[#00c9e1] transition-colors cursor-pointer" data-testid="link-forgot-password">
                      Forgot password?
                    </button>
                    <span className="text-[12px] text-[#6B7280] cursor-default" title="Coming soon">
                      Create account <span className="text-[10px]">(soon)</span>
                    </span>
                  </div>
                </motion.form>
              )}

              {/* REGISTER — disabled, coming soon */}
              {mode === 'register' && (
                <motion.div key="register" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="space-y-4 text-center" data-testid="form-register">
                  <BackBtn target={backTarget[mode]} />

                  <div className="py-8 space-y-3">
                    <div className="text-3xl">🚀</div>
                    <h3 className="text-lg font-semibold text-[#F3F4F6]">Coming Soon</h3>
                    <p className="text-[14px] text-[#9CA3AF] leading-relaxed max-w-[280px] mx-auto">
                      Public registration is not yet available. Contact us for early access.
                    </p>
                  </div>

                  <p className="text-center text-[13px] text-[#9CA3AF] pt-1">
                    Already have an account?{' '}
                    <button type="button" onClick={() => switchMode('login')} className="text-[#F3F4F6] font-medium hover:text-[#00B2C8] transition-colors cursor-pointer" data-testid="link-back-to-login">
                      Sign in
                    </button>
                  </p>
                </motion.div>
              )}

              {/* CONFIRM */}
              {mode === 'confirm' && (
                <motion.form key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onSubmit={handleConfirm} className="space-y-4" data-testid="form-confirm">
                  <BackBtn target={backTarget[mode]} />

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="confirmation-code">Confirmation Code</FieldLabel>
                    <ColonyInput id="confirmation-code" placeholder="000000" value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} required centered testId="input-confirmation-code" />
                  </div>

                  <PrimaryBtn type="submit" disabled={isLoading || !confirmationCode} loading={isLoading} testId="button-confirm">
                    Verify email
                  </PrimaryBtn>
                </motion.form>
              )}

              {/* FORGOT */}
              {mode === 'forgot' && (
                <motion.form key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onSubmit={handleForgotPassword} className="space-y-4" data-testid="form-forgot">
                  <BackBtn target={backTarget[mode]} />

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="forgot-email">Email</FieldLabel>
                    <ColonyInput id="forgot-email" type="email" autoComplete="username" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required testId="input-forgot-email" />
                  </div>

                  <PrimaryBtn type="submit" disabled={isLoading || !email} loading={isLoading} testId="button-forgot">
                    Send reset code
                  </PrimaryBtn>
                </motion.form>
              )}

              {/* RESET */}
              {mode === 'reset' && (
                <motion.form key="reset" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onSubmit={handleResetPassword} className="space-y-4" data-testid="form-reset">
                  <BackBtn target={backTarget[mode]} />

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="reset-code">Reset Code</FieldLabel>
                    <ColonyInput id="reset-code" placeholder="000000" value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} required centered testId="input-reset-code" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="new-password">New Password</FieldLabel>
                    <ColonyInput id="new-password" type="password" autoComplete="new-password" placeholder="Enter new password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="input-new-password" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="confirm-new-password">Confirm Password</FieldLabel>
                    <ColonyInput id="confirm-new-password" type="password" autoComplete="new-password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required testId="input-confirm-new-password" />
                    {showPasswordMismatch && (
                      <p className="text-[#E5534B] text-[13px] flex items-center gap-1" data-testid="text-password-mismatch-reset">
                        <AlertCircle className="w-3 h-3" /> Passwords do not match
                      </p>
                    )}
                  </div>

                  <PrimaryBtn type="submit" disabled={isLoading || !passwordsMatch || !confirmationCode || !password} loading={isLoading} testId="button-reset">
                    Reset password
                  </PrimaryBtn>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Skip login — for local/offline use while auth isn't set up */}
        <p className="text-center mt-4">
          <button
            type="button"
            onClick={skipLogin}
            className="text-[12px] text-[#6B7280] hover:text-[#9CA3AF] transition-colors cursor-pointer underline underline-offset-2"
            data-testid="button-skip-login"
          >
            Skip for now
          </button>
        </p>

        {/* Terms footer */}
        <p className="text-center text-[11px] text-[#9CA3AF] mt-5 leading-relaxed px-4">
          By continuing, you agree to Colony's{' '}
          <button className="text-[#D1D5DB] hover:text-[#F3F4F6] transition-colors cursor-pointer">Terms of Service</button>
          {' '}and{' '}
          <button className="text-[#D1D5DB] hover:text-[#F3F4F6] transition-colors cursor-pointer">Privacy Policy</button>
        </p>
      </div>
    </div>
  );
}
